import { opposite, step } from "./direction";
import { key, type Maze } from "./maze";
import { TUNING, type Game } from "./game";
import { DIRECTIONS, type Direction, type Tile } from "./types";

/**
 * A pilot for the astronaut.
 *
 * This exists so the game can be *driven and verified* rather than merely
 * rendered: the Playwright suite runs this advisor and presses the real arrow
 * keys with its answer, so what is recorded is the actual game responding to
 * actual input.
 *
 * It is deliberately simple and readable, not optimal:
 *   - flood the deck from the astronaut, refusing tiles that are about to be
 *     occupied by hunting slop;
 *   - prefer frightened slop (they are worth 200-1600), then the nearest
 *     5h Quota Reset when slop is closing, then the nearest Story Point;
 *   - if every route is cut off, run to whichever neighbour maximises the
 *     distance to the closest slop.
 */

export interface PilotOptions {
  /** Tiles within this ghost-distance count as lethal and are routed around. */
  readonly dangerRadius?: number;
  /** Grab a 5h Quota Reset when slop is at most this far away. */
  readonly panicRadius?: number;
  /**
   * Play for the slop rather than for the clock.
   *
   * Saves 5h Quota Resets until slop are actually out, then commits to chasing
   * the frightened ones across the whole deck instead of only nearby ones.
   * This is how you send all four home in one Review.
   */
  readonly huntSlop?: boolean;
  /**
   * Leave this many Story Points on the deck while there is still hunting to
   * do.
   *
   * Without it the pilot can simply clear the level before the last 5h Quota
   * Reset has been cashed in, and the round ends with slop never caught. The
   * reserve is only held back while `unfinishedBusiness` is true.
   */
  readonly reserveStoryPoints?: number;
  /** True while there are still slop the caller wants eaten. */
  readonly unfinishedBusiness?: boolean;
}

const DEFAULTS = { dangerRadius: 4, panicRadius: 8 } as const;

/**
 * 5h Quota Resets held back from the hunt for the endgame.
 *
 * MEASURED at zero. Holding two back sounds prudent -- the run does die in the
 * endgame -- but Resets are also the only way to make slop edible, so reserving
 * them cost more catches than it saved lives: 2 of 6 seeds finished with all
 * four caught, against 3 of 6 when spending freely.
 */
const ENDGAME_RESERVE = 0;

interface Flood {
  readonly dist: Map<string, number>;
  readonly firstStep: Map<string, Direction>;
}

/** Breadth-first flood over tiles the player may walk, skipping `blocked`. */
const floodFrom = (maze: Maze, origin: Tile, blocked: ReadonlySet<string>): Flood => {
  const dist = new Map<string, number>([[key(origin), 0]]);
  const firstStep = new Map<string, Direction>();
  const queue: Tile[] = [origin];

  while (queue.length > 0) {
    const cur = queue.shift()!;
    const curKey = key(cur);
    const d = dist.get(curKey)!;
    for (const dir of DIRECTIONS) {
      const next = maze.normalise(step(cur, dir));
      const nextKey = key(next);
      if (!maze.isOpen(next, false) || dist.has(nextKey) || blocked.has(nextKey)) continue;
      dist.set(nextKey, d + 1);
      // Inherit the opening move, so the answer is one arrow key.
      firstStep.set(nextKey, d === 0 ? dir : firstStep.get(curKey)!);
      queue.push(next);
    }
  }
  return { dist, firstStep };
};

/**
 * Tiles each hunting slop can reach within `radius`, as a lethal zone.
 *
 * Directional on purpose: a slop cannot reverse, so the tile behind it is not
 * a threat for at least a lap of the block. Treating the zone as a plain disc
 * both over-states the danger — walling off corridors that are in fact the
 * safest route, which leaves the pilot cornered — and under-states how far
 * ahead a slop reaches along the corridor it is already committed to.
 */
export const dangerZone = (game: Game, radius: number): Set<string> => {
  const zone = new Set<string>();
  for (const ghost of game.ghosts) {
    if (ghost.state !== "hunting" || ghost.frightened) continue;
    const start = ghost.tile;
    const seen = new Map<string, number>([[key(start), 0]]);
    const queue: Tile[] = [start];
    zone.add(key(start));
    while (queue.length > 0) {
      const cur = queue.shift()!;
      const d = seen.get(key(cur))!;
      if (d >= radius) continue;
      for (const dir of DIRECTIONS) {
        // Only the first step is constrained: after one move the slop is free
        // to turn, so everything beyond spreads normally.
        if (d === 0 && dir === opposite(ghost.dir)) continue;
        const next = game.maze.normalise(step(cur, dir));
        if (!game.maze.isOpen(next, false) || seen.has(key(next))) continue;
        seen.set(key(next), d + 1);
        zone.add(key(next));
        queue.push(next);
      }
    }
  }
  return zone;
};

/** Grid distance to the nearest hunting slop, or Infinity when none are out. */
export const nearestThreatDistance = (game: Game, from: Tile): number => {
  let best = Number.POSITIVE_INFINITY;
  for (const ghost of game.ghosts) {
    if (ghost.state !== "hunting" || ghost.frightened) continue;
    const d = game.maze.pathLength(from, ghost.tile, false);
    if (d >= 0) best = Math.min(best, d);
  }
  return best;
};

/**
 * The next arrow key to press, or null when the pilot sees no legal move.
 *
 * Pure: it reads the game but never mutates it.
 */
export const pilot = (game: Game, options: PilotOptions = {}): Direction | null => {
  const dangerRadius = options.dangerRadius ?? DEFAULTS.dangerRadius;
  const panicRadius = options.panicRadius ?? DEFAULTS.panicRadius;

  const here = game.player.tile;
  const danger = dangerZone(game, dangerRadius);
  danger.delete(key(here)); // never refuse to leave the tile we are standing on

  const safe = floodFrom(game.maze, here, danger);
  const anywhere = floodFrom(game.maze, here, new Set());
  const { storyPoints, quotaResets } = game.remainingPellets();

  const pick = (goals: readonly Tile[], flood: Flood): { dir: Direction; dist: number } | null => {
    let best: { dir: Direction; dist: number } | null = null;
    for (const goal of goals) {
      const d = flood.dist.get(key(goal));
      const dir = flood.firstStep.get(key(goal));
      if (d === undefined || dir === undefined) continue;
      if (best === null || d < best.dist) best = { dir, dist: d };
    }
    return best;
  };

  // 1. Frightened slop are the biggest prize on the board.
  const edible = game.ghosts.filter((g) => g.frightened && g.state === "hunting").map((g) => g.tile);
  const threatNow = nearestThreatDistance(game, here);
  // The `anywhere` flood ignores the danger zone, so falling back to it means
  // walking a route a hunting slop can reach. Worth it only when none is close
  // enough to matter -- chasing a prize through a corridor that is about to be
  // occupied is exactly how the run ends with two slop uneaten and no lives.
  const recklessOk = options.huntSlop === true && threatNow > 8;
  const hunt = pick(edible, safe) ?? (recklessOk ? pick(edible, anywhere) : null);
  if (hunt !== null) {
    // Only commit to a chase that can actually finish inside the window. Running
    // down a slop that un-frightens on arrival is how the pilot gets killed.
    const reachable = hunt.dist <= game.frightenedLeft * TUNING.playerSpeed * 0.75;
    if (options.huntSlop === true ? reachable : hunt.dist <= 10) return hunt.dir;
  }

  // Same rule as the chase: only accept a route that ignores the danger zone
  // when nothing is near enough to punish it.
  const quotaReset = pick(quotaResets, safe) ?? (threatNow > 8 ? pick(quotaResets, anywhere) : null);

  // 2a. Hunt mode: spend a Reset, but only when it will actually pay.
  //
  // Taking one the moment any slop is out burns all four early, each catching
  // a single slop, and leaves the endgame with no Resets at all -- which is
  // exactly how the run dies with three caught and a handful of Story Points
  // uncollected. So: only spend one when at least two slop are close enough to
  // chase on the same window, and never spend the LAST one while a long
  // endgame still has to be walked.
  if (options.huntSlop === true && quotaReset !== null) {
    const nearby = game.ghosts.filter(
      (g) => g.state === "hunting" && !g.frightened && game.maze.pathLength(here, g.tile, false) <= 14,
    ).length;
    // Keep a couple back for the endgame. Spending all four on the hunt leaves
    // the last stretch -- crossing the deck for scattered Story Points with
    // four slop loose and no Reset to fall back on -- and that is where the
    // run actually dies, with three caught and a handful of points left.
    const spare = quotaResets.length > ENDGAME_RESERVE;
    if (nearby >= 2 && spare) return quotaReset.dir;
  }

  // 2b. Otherwise: slop closing in and a 5h Quota Reset within reach, take it.
  if (quotaReset !== null && threatNow <= panicRadius && quotaReset.dist <= panicRadius) return quotaReset.dir;

  // 3. Otherwise sweep up Story Points, preferring routes that stay clear.
  //
  // Unless we are holding a reserve: clearing the last few Story Points ends
  // the level, so while there is hunting left to do the pilot deliberately
  // leaves some on the deck and circles instead.
  const reserve = options.reserveStoryPoints ?? 0;
  const holding = options.unfinishedBusiness === true && storyPoints.length <= reserve;
  const collectable = holding ? [] : [...storyPoints, ...quotaResets];
  const safeGoal = pick(collectable, safe);
  if (safeGoal !== null) return safeGoal.dir;

  // Holding the reserve: keep moving, and keep away from the slop, until the
  // caller says the hunting is done.
  if (holding) {
    const stall = bestEscape(game, here);
    if (stall !== null) return stall;
  }

  // 4. Every pellet is behind a wall of slop. Take the least dangerous exit.
  const escape = bestEscape(game, here);
  if (escape !== null) return escape;

  // 5. Nothing safe at all; head for the nearest pellet regardless.
  return pick(collectable.length > 0 ? collectable : [...storyPoints, ...quotaResets], anywhere)?.dir ?? null;
};

/** The neighbour that puts the most distance between us and the nearest slop. */
export const bestEscape = (game: Game, from: Tile): Direction | null => {
  let best: { dir: Direction; score: number } | null = null;
  for (const dir of DIRECTIONS) {
    const next = game.maze.normalise(step(from, dir));
    if (!game.maze.isOpen(next, false)) continue;
    const score = nearestThreatDistance(game, next);
    if (best === null || score > best.score) best = { dir, score };
  }
  return best?.dir ?? null;
};
